import debounce from 'debounce-promise';
import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type ComponentProps,
    type CSSProperties,
    type ReactNode,
    type Ref,
} from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import type {
    ClearIndicatorProps,
    DropdownIndicatorProps,
    GroupBase,
    MultiValueProps,
    MultiValueRemoveProps,
    OptionProps,
    PlaceholderProps,
    SelectInstance,
    MenuProps as SelectMenuProps,
} from 'react-select';
import {
    OnChangeValue,
    default as ReactSelect,
    components as SelectComponents,
    SelectComponentsConfig,
    ValueContainerProps,
} from 'react-select';
import { default as ReactSelectAsync } from 'react-select/async';
import { default as ReactSelectAsyncCreatable } from 'react-select/async-creatable';
import { default as ReactSelectCreatable } from 'react-select/creatable';
import IconAction from '~/components/new/IconAction';
import {
    MenuRowAction,
    MenuRowCheckbox,
    MenuRowCustom,
    MenuRowLink,
    MenuRowTag,
    MenuRowToggle,
    type MenuProps,
    type MenuRowActionProps,
    type MenuRowCheckboxProps,
    type MenuRowLinkProps,
    type MenuRowTagProps,
    type MenuRowToggleProps,
} from '~/components/new/Menu';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import { reportError } from '~/monitoring.ts';
import { toSelectOptions, toSelectValue } from '~/utils/forms';
import { setRemixiconAsCSSVar } from '~/utils/icons';
import type { DataTestAttributes } from '~/utils/qa';
import { useMergeRefs } from '~/utils/react';
import { classNames } from '~/utils/styles';
import { z } from '~/utils/zIndex';

type MenuRowType = Omit<MenuProps['typology'], 'custom'>;

export type SelectOption<T extends string | number = any, L = any> = {
    label: L;
    value: T;
    type?: MenuRowType;
    ['data-test-name']?: string;
};

type MultiValueType<T extends string | number> =
    | {
          isMulti?: false;
          value?: T | SelectOption<T> | null | GroupBase<SelectOption<T>>;
          onChange?: (v: SelectOption<T>) => void;
      }
    | {
          isMulti: true;
          value?: SelectOption<T>[];
          onChange?: (v: SelectOption<T>[]) => void;
      };

// TODO: remove when we split props for ReactSelectAsyncCreatable, ReactSelectCreatable, ReactSelect and ReactSelectAsync
type UnknownProps = Record<string, any>;

type RowActionOption<T extends string | number = string | number> = SelectOption<T> &
    MenuRowActionProps;
type RowCheckboxOption<T extends string | number = string | number> = SelectOption<T> &
    MenuRowCheckboxProps & {
        onChange?: () => void;
    };
type RowLinkOption<T extends string | number = string | number> = SelectOption<T> &
    MenuRowLinkProps;
type RowTagOption<T extends string | number = string | number> = SelectOption<T> & MenuRowTagProps;
type RowToggleOption<T extends string | number = string | number> = SelectOption<T> &
    MenuRowToggleProps & {
        onChange?: () => void;
    };

export type RowOption<T extends string | number = any> =
    | RowActionOption<T>
    | RowCheckboxOption<T>
    | RowLinkOption<T>
    | RowTagOption<T>
    | RowToggleOption<T>;

export const SELECT_CONTROL_MAX_WIDTH = 280;
export const SELECT_MENU_MIN_WIDTH = 200;
export const SELECT_MENU_MAX_WIDTH = 400;

export function estimateMenuWidth(controlWidth: number, growable = true): number {
    if (!growable) {
        return controlWidth;
    }

    return Math.min(Math.max(controlWidth, SELECT_MENU_MIN_WIDTH), SELECT_MENU_MAX_WIDTH);
}

export function shouldAlignMenuRight(
    controlRect: Pick<DOMRect, 'left'>,
    menuWidth: number,
    windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0,
): boolean {
    return controlRect.left + menuWidth > windowWidth;
}

export function isReliableMenuWidth(menuWidth: number): boolean {
    return menuWidth >= 1;
}

export type SelectProps<T extends string | number> = MultiValueType<T> & {
    options?: (number | string | RowOption<T> | GroupBase<SelectOption<T>>)[];
    defaultOptions?: (number | string | RowOption<T> | GroupBase<SelectOption<T>>)[];
    disabled?: boolean;
    error?: boolean;
    className?: string;
    debounceMillis?: number;
    creatable?: boolean;
    loadOptions?: (value: string) => Promise<SelectOption<T>[]>;
    growable?: boolean;
    classNamePrefix?: string;
    label?: string;
    googlePlacesAutocomplete?: boolean;
} & DataTestAttributes &
    UnknownProps;

export default function Select<T extends string | number>({
    disabled,
    error,
    className = '',
    debounceMillis = 250,
    creatable = false,
    loadOptions,
    label = '',
    classNamePrefix = 'select-input',
    ref,
    googlePlacesAutocomplete = false,
    ['data-test-component']: testComponent = 'ui/forms/Select',
    ['data-test-name']: testName,
    ...props
}: SelectProps<T>) {
    const [alignRight, setAlignRight] = useState(false);
    const [openUp, setOpenUp] = useState(false);
    const menuObserver = useRef<IntersectionObserver | null>(null);
    const menuResizeObserver = useRef<ResizeObserver | null>(null);
    const loadOptionsGeneration = useRef(0);
    const selectRef = useMergeRefs<SelectInstance>(ref as Ref<SelectInstance> | undefined);
    const growable = props.growable !== false;

    const updateMenuAlignment = (menuWidth?: number) => {
        const select = selectRef.current;
        const controlRect = select?.controlRef?.getBoundingClientRect();
        const menuList = select?.menuListRef;
        if (!controlRect) {
            return;
        }

        if (select.props?.isLoading) {
            return;
        }

        const hasConnectedMenuList = Boolean(menuList?.isConnected);

        if (!hasConnectedMenuList && menuWidth !== undefined) {
            return;
        }

        if (menuWidth !== undefined && !isReliableMenuWidth(menuWidth)) {
            return;
        }

        const width =
            menuWidth ??
            (hasConnectedMenuList
                ? menuList!.getBoundingClientRect().width
                : estimateMenuWidth(controlRect.width, growable));

        setAlignRight(shouldAlignMenuRight(controlRect, width));
    };

    const disconnectMenuObservers = () => {
        menuObserver.current?.disconnect();
        menuObserver.current = null;
        menuResizeObserver.current?.disconnect();
        menuResizeObserver.current = null;
    };

    const observeMenuList = (menuList: HTMLElement) => {
        updateMenuAlignment(menuList.getBoundingClientRect().width);

        disconnectMenuObservers();

        if (typeof IntersectionObserver !== 'undefined') {
            menuObserver.current = new IntersectionObserver((entries) => {
                const entry = entries[0];
                const menuRect = entry.boundingClientRect;
                updateMenuAlignment(menuRect.width);

                if (menuRect.bottom > entry.intersectionRect.bottom) {
                    setOpenUp(true);
                }
            });
            menuObserver.current.observe(menuList);
        }

        if (typeof ResizeObserver !== 'undefined') {
            menuResizeObserver.current = new ResizeObserver((entries) => {
                const width = entries[0]?.contentRect.width;
                if (width) {
                    updateMenuAlignment(width);
                }
            });
            menuResizeObserver.current.observe(menuList);
        }
    };

    const waitForMenuList = (maxChecks = 10): Promise<HTMLElement | null> =>
        new Promise((resolve) => {
            let checks = 0;
            const check = () => {
                const menuList = selectRef.current?.menuListRef;
                if (menuList || checks >= maxChecks) {
                    resolve(menuList ?? null);
                } else {
                    checks++;
                    requestAnimationFrame(check);
                }
            };
            requestAnimationFrame(check);
        });

    const waitForMenuReady = (maxChecks = 10): Promise<HTMLElement | null> =>
        new Promise((resolve) => {
            let checks = 0;
            const check = () => {
                const select = selectRef.current;
                const menuList = select?.menuListRef;
                const isReady = Boolean(menuList?.isConnected && !select?.props?.isLoading);
                if (isReady || checks >= maxChecks) {
                    resolve(isReady ? menuList! : null);
                } else {
                    checks++;
                    requestAnimationFrame(check);
                }
            };
            requestAnimationFrame(check);
        });

    const onMenuOpen = async () => {
        if (!selectRef.current?.controlRef) {
            return;
        }

        updateMenuAlignment();

        // menuListRef is not synchronously populated on onMenuOpen callback https://github.com/JedWatson/react-select/issues/4243
        const menuList = await waitForMenuList();
        if (menuList) {
            observeMenuList(menuList);
        }

        props?.onMenuOpen?.();
    };

    const onMenuClose = () => {
        // In controlled mode (menuIsOpen is a boolean), defer alignment reset
        // to the effect below so the menu doesn't jump when the consumer
        // keeps the menu open (e.g. FilterCheckboxOptions input focus).
        if (props.menuIsOpen === undefined) {
            setAlignRight(false);
            setOpenUp(false);
            disconnectMenuObservers();
        }

        props?.onMenuClose?.();
    };

    // Reset alignment when a controlled menu actually closes.
    useEffect(() => {
        if (props.menuIsOpen === false) {
            setAlignRight(false);
            setOpenUp(false);
            disconnectMenuObservers();
        }
    }, [props.menuIsOpen]);

    // Controlled menus mount after onMenuOpen, so alignment must rerun once open.
    useEffect(() => {
        if (props.menuIsOpen !== true) {
            return;
        }

        updateMenuAlignment();

        void waitForMenuList().then((menuList) => {
            if (menuList?.isConnected && selectRef.current?.props?.menuIsOpen === true) {
                observeMenuList(menuList);
            }
        });
    }, [props.menuIsOpen]);

    useEffect(() => {
        setRemixiconAsCSSVar('--search-select-icon', RemixIconNames.RiSearch2Line);
    }, []);

    const components: Partial<SelectComponentsConfig<any, any, any>> = useMemo(
        () => ({
            ...(props?.components ?? {}),
            DropdownIndicator: (props: DropdownIndicatorProps) => {
                if (props.selectProps.isLoading) {
                    return (
                        <Icon
                            className='indicator-loading'
                            icon='loading'
                            variant='secondary'
                        />
                    );
                }

                return (
                    <Icon
                        className='indicator-dropdown'
                        icon={
                            props.selectProps.menuIsOpen
                                ? RemixIconNames.RiArrowDropUpLine
                                : RemixIconNames.RiArrowDropDownLine
                        }
                    />
                );
            },
            ClearIndicator:
                props?.components?.ClearIndicator ??
                ((clearProps: ClearIndicatorProps) => (
                    <SelectComponents.ClearIndicator
                        {...clearProps}
                        innerProps={{
                            ...clearProps.innerProps,
                            // @ts-expect-error - data-test-component is not typed
                            ['data-test-component']: 'ui/forms/Select/ClearIndicator',
                        }}>
                        <IconAction
                            icon={RemixIconNames.RiCloseCircleFill}
                            title=''
                            colorVar='core-tertiary'
                            hoverColorVar='core-primary'
                        />
                    </SelectComponents.ClearIndicator>
                )),
            LoadingIndicator: () => null,
            LoadingMessage: () => null,
            Menu:
                props?.components?.Menu ??
                ((menuProps: SelectMenuProps) => {
                    if (menuProps.selectProps.isLoading) {
                        return null;
                    }

                    return <SelectComponents.Menu {...menuProps} />;
                }),
            Option:
                props?.components?.Option ??
                (({
                    innerProps,
                    ...optionProps
                }: OptionProps<
                    | MenuRowActionProps
                    | MenuRowLinkProps
                    | MenuRowCheckboxProps
                    | MenuRowTagProps
                    | MenuRowToggleProps
                >) => {
                    const onClick = optionProps.data.onClick
                        ? optionProps.data.onClick
                        : innerProps.onClick;
                    const { type: optionType = 'action' } = optionProps.data as RowOption;

                    // force scroll into view when using portal
                    // See (bug): https://github.com/JedWatson/react-select/issues/5775
                    const optionRef = useRef<HTMLDivElement>(null!);
                    useEffect(() => {
                        if (optionProps.isSelected && optionRef.current) {
                            optionRef.current.scrollIntoView();
                        }
                    }, [optionProps.isSelected]);

                    return (
                        <SelectComponents.Option
                            {...optionProps}
                            innerRef={(ref) => {
                                optionRef.current = ref!;
                            }}
                            innerProps={{
                                ...innerProps,
                                // @ts-expect-error - data-test-component is not typed
                                ['data-test-component']: 'ui/forms/Select/Option',
                                onClick: !optionProps.data.disabled ? onClick : undefined,
                            }}
                            className='menu-row'>
                            {optionType === 'action' && (
                                <MenuRowAction
                                    label={optionProps.data.label}
                                    disabled={optionProps.data.disabled}
                                    icon={optionProps.data.icon}
                                    info={(optionProps.data as MenuRowActionProps).info}
                                    selected={optionProps.isSelected}
                                    className={optionProps.data.className}
                                />
                            )}

                            {optionType === 'link' && (
                                <MenuRowLink
                                    onClick={(e) => onClick?.(e)}
                                    label={optionProps.data.label}
                                    disabled={optionProps.data.disabled}
                                    icon={optionProps.data.icon}
                                    info={(optionProps.data as MenuRowLinkProps).info}
                                    className={optionProps.data.className}
                                />
                            )}

                            {optionType === 'checkbox' && (
                                <MenuRowCheckbox
                                    label={optionProps.data.label}
                                    disabled={optionProps.data.disabled}
                                    icon={optionProps.data.icon}
                                    checked={(optionProps.data as RowCheckboxOption).checked}
                                    onClick={() =>
                                        (optionProps.data as RowCheckboxOption).onChange?.()
                                    }
                                    className={optionProps.data.className}
                                />
                            )}

                            {optionType === 'tag' && (
                                <MenuRowTag
                                    label={optionProps.data.label}
                                    variant={(optionProps.data as MenuRowTagProps).variant}
                                    color={(optionProps.data as MenuRowTagProps).color}
                                    disabled={optionProps.data.disabled}
                                    icon={optionProps.data.icon}
                                    selected={optionProps.isSelected}
                                    className={optionProps.data.className}
                                />
                            )}

                            {optionType === 'toggle' && (
                                <MenuRowToggle
                                    label={optionProps.data.label}
                                    disabled={optionProps.data.disabled}
                                    icon={optionProps.data.icon}
                                    info={(optionProps.data as MenuRowToggleProps).info}
                                    selected={(optionProps.data as MenuRowToggleProps).selected}
                                    onClick={() =>
                                        (optionProps.data as RowToggleOption).onChange?.()
                                    }
                                    className={optionProps.data.className}
                                />
                            )}

                            {optionType === 'custom' && <MenuRowCustom {...optionProps.data} />}
                        </SelectComponents.Option>
                    );
                }),
            MultiValue: (props: MultiValueProps) => (
                <SelectComponents.MultiValue
                    {...props}
                    innerProps={{
                        ...props.innerProps,
                        // @ts-expect-error - data-test-component is not typed
                        ['data-test-component']: 'ui/forms/Select/MultiValue',
                    }}
                />
            ),
            MultiValueRemove: (props: MultiValueRemoveProps) => (
                <SelectComponents.MultiValueRemove
                    {...props}
                    innerProps={{
                        ...props.innerProps,
                        // @ts-expect-error - data-test-component is not typed
                        ['data-test-component']: 'ui/forms/Select/MultiValueRemove',
                    }}>
                    <Icon
                        className='indicator-remove'
                        icon={RemixIconNames.RiCloseCircleFill}
                    />
                </SelectComponents.MultiValueRemove>
            ),
            Placeholder: (props: PlaceholderProps) => (
                <SelectComponents.Placeholder
                    {...props}
                    innerProps={{
                        ...props.innerProps,
                        // @ts-expect-error - data-test-component is not typed
                        ['data-test-component']: 'ui/forms/Select/Placeholder',
                    }}
                />
            ),
        }),
        [props?.components],
    );
    if (label) {
        components.ValueContainer = LabelValueContainer;
    } else if (!components.ValueContainer) {
        components.ValueContainer = ValueContainer;
    }

    const selectProps = {
        ref: selectRef,
        className: classNames({ 'select-with-error': !!error, [className]: Boolean(className) }),
        classNamePrefix,
        isDisabled: disabled,
        styles: {
            control: (base: CSSProperties) => ({
                ...base,
                maxWidth: SELECT_CONTROL_MAX_WIDTH,
            }),
            singleValue: (base: CSSProperties) => ({
                ...base,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }),
            menu: (base: CSSProperties) => {
                const menuAlignment = alignRight
                    ? { left: 'unset', right: 0 }
                    : { left: 0, right: 'unset' };

                if (!growable) {
                    return {
                        ...base,
                        zIndex: z.dropdown,
                        ...menuAlignment,
                    };
                }

                const controlWidth = selectRef.current?.controlRef?.getBoundingClientRect().width;
                const minWidth = Math.max(controlWidth ?? 0, SELECT_MENU_MIN_WIDTH);

                return {
                    ...base,
                    zIndex: z.dropdown,
                    width: 'max-content',
                    minWidth,
                    maxWidth: SELECT_MENU_MAX_WIDTH,
                    // NOTE(toqueteos): The menu can partially be shown out of the screen when content is wider than the trigger.
                    // Solution https://github.com/JedWatson/react-select/issues/3491#issuecomment-748506493
                    // Ticket https://github.com/remotely-works/platform/issues/3701
                    ...menuAlignment,
                };
            },
            ...props?.styles,
        },
        ...props,
        options: isUnsupportedOptions(props as SelectProps<T>)
            ? toSelectOptions(
                  ((props as SelectProps<T>).options as (string | number | SelectOption<T>)[]) ||
                      [],
              )
            : props.options,
        value: isUnsupportedValue(props as SelectProps<T>)
            ? buildSupportedValue(props as SelectProps<T>)
            : props.value,
        // prevents an option of type 'toggle', 'checkbox' or 'link' to be selected
        isOptionDisabled: (option: RowOption) =>
            option.type === 'toggle' || option.type === 'checkbox' || option.type === 'link',
        components,
        loadingMessage: () => null,
        noOptionsMessage:
            props.noOptionsMessage ?? (() => (props.isSearchable === false ? 'No results' : null)),
        label,
        onMenuOpen,
        onMenuClose,
        loadOptions: loadOptions
            ? debounce(async (inputValue: string) => {
                  if (inputValue.trim() === '') return;
                  const generation = ++loadOptionsGeneration.current;
                  return loadOptions(inputValue).then((v) => {
                      // Defer until react-select applies options and renders the menu.
                      requestAnimationFrame(() => {
                          void waitForMenuReady(60).then((menuList) => {
                              if (generation !== loadOptionsGeneration.current) {
                                  return;
                              }
                              if (menuList) {
                                  observeMenuList(menuList);
                              }
                          });
                      });
                      return v;
                  });
              }, debounceMillis)
            : undefined,
        menuPortalTarget: (() => {
            const target = props.menuPortalTarget;
            if (target instanceof HTMLElement) {
                return target;
            }
            if (typeof target === 'string') {
                return document.querySelector(target) ?? document.body;
            }
            return document.querySelector('#select-portal') ?? document.body;
        })(),
        menuPosition: 'fixed',
        menuPlacement: openUp ? 'top' : 'bottom',
        classNames: {
            ...(props.classNames ?? {}),
            menu: (state: SelectMenuProps<RowOption<T>>) =>
                [props.classNames?.menu?.(state) ?? ''].filter(Boolean).join(' '),
        },
    };

    if (loadOptions) {
        if (creatable) {
            return (
                <div
                    data-test-component={testComponent}
                    data-test-name={testName}>
                    <ReactSelectAsyncCreatable
                        {...(selectProps as unknown as ComponentProps<
                            typeof ReactSelectAsyncCreatable
                        >)}
                    />
                </div>
            );
        }

        return (
            <div
                data-test-component={testComponent}
                data-test-name={testName}>
                <ReactSelectAsync
                    {...(selectProps as unknown as ComponentProps<typeof ReactSelectAsync>)}
                />
            </div>
        );
    }

    if (creatable) {
        return (
            <div
                data-test-component={testComponent}
                data-test-name={testName}>
                <ReactSelectCreatable
                    {...(selectProps as unknown as ComponentProps<typeof ReactSelectCreatable>)}
                />
            </div>
        );
    }

    if (googlePlacesAutocomplete) {
        return (
            <GooglePlacesAutocomplete
                apiKey={MAPS_API_KEY}
                apiOptions={{ language: 'en', region: 'us' }}
                autocompletionRequest={{ types: ['(cities)'] }}
                debounce={750}
                selectProps={selectProps as any}
            />
        );
    }

    return (
        <div
            data-test-component={testComponent}
            data-test-name={testName}>
            <ReactSelect {...(selectProps as unknown as ComponentProps<typeof ReactSelect>)} />
        </div>
    );
}
const MAPS_API_KEY = 'AIzaSyBljwfRZ9YobjXhQCV9rxCoMz7AdlxfKUk';

type LabelValueContainerProps = {
    label: string;
};

export function selectedValueLabel(
    props: Pick<ValueContainerProps, 'getValue' | 'selectProps'>,
): ReactNode {
    const selected = props.getValue();
    if (!selected || selected.length === 0) {
        return undefined;
    }
    return props.selectProps.getOptionLabel?.(selected[0]);
}

export function PlaceholderSizer({
    classNamePrefix: classNamePrefixProp,
    hasValue,
    inputValue,
    isMulti,
    isSearchable,
    placeholder,
    valueLabel,
}: {
    classNamePrefix?: string | null;
    hasValue: boolean;
    inputValue: string;
    isMulti?: boolean;
    isSearchable?: boolean;
    placeholder?: ReactNode;
    valueLabel?: ReactNode;
}) {
    // While typing react-select hides the placeholder (no value) or the single
    // value (existing value), which lets the control shrink to the typed text.
    // Render whichever was visible at rest into a hidden sizer so the control
    // keeps at least that width (still capped by the control's max-width).
    const content = hasValue ? valueLabel : placeholder;

    if (
        isMulti ||
        isSearchable === false ||
        !inputValue ||
        content === undefined ||
        content === null ||
        content === ''
    ) {
        return null;
    }

    const classNamePrefix = classNamePrefixProp ?? 'select-input';

    return (
        <span
            className={`${classNamePrefix}__placeholder-sizer`}
            aria-hidden>
            {content}
        </span>
    );
}

function ValueContainer({
    children,
    ['data-test-component']: testComponent = 'ui/forms/Select/ValueContainer',
    ['data-test-name']: testName,
    ...props
}: ValueContainerProps & DataTestAttributes) {
    const { selectProps, hasValue } = props;

    return (
        <div
            data-test-component={testComponent}
            data-test-name={testName}
            className='select-input__value-container select-input__value-container--is-multi css-hlgwow'>
            <SelectComponents.ValueContainer {...props}>
                <PlaceholderSizer
                    classNamePrefix={selectProps.classNamePrefix}
                    hasValue={hasValue}
                    inputValue={selectProps.inputValue}
                    isMulti={selectProps.isMulti}
                    isSearchable={selectProps.isSearchable}
                    placeholder={selectProps.placeholder}
                    valueLabel={selectedValueLabel(props)}
                />
                {children}
            </SelectComponents.ValueContainer>
        </div>
    );
}

function LabelValueContainer({
    children,
    ['data-test-component']: testComponent = 'ui/forms/Select/LabelValueContainer',
    ['data-test-name']: testName,
    ...props
}: ValueContainerProps & DataTestAttributes) {
    const { selectProps } = props;
    // you can pass any props to the Selects, but it's a mess to get the types right
    // so we can just assume it's there.
    const label = (selectProps as unknown as LabelValueContainerProps).label;
    return (
        <div
            className='select-input-labeled'
            data-test-component={testComponent}
            data-test-name={testName}>
            <span className='select-input-labeled-label'>{label}</span>
            <SelectComponents.ValueContainer {...props}>
                <PlaceholderSizer
                    classNamePrefix={selectProps.classNamePrefix}
                    hasValue={props.hasValue}
                    inputValue={selectProps.inputValue}
                    isMulti={selectProps.isMulti}
                    isSearchable={selectProps.isSearchable}
                    placeholder={selectProps.placeholder}
                    valueLabel={selectedValueLabel(props)}
                />
                {children}
            </SelectComponents.ValueContainer>
        </div>
    );
}

export type YearSelectorProps = {
    value: number;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    onChange: (v: string | number) => void;
    placeholder?: string;
    max?: number;
    min?: number;
};

export function YearSelector({
    value,
    className = '',
    disabled,
    error,
    onChange,
    placeholder,
    max = new Date().getFullYear(),
    min = 1960,
}: YearSelectorProps) {
    const options = [];
    for (let i = max; i >= min; i--) {
        options.push({ label: `${i}`, value: i });
    }
    return (
        <Select
            className={className}
            options={options}
            onChange={(v: OnChangeValue<SelectOption<string | number>, false>) =>
                onChange(v!.value)
            }
            disabled={disabled}
            placeholder={placeholder}
            value={options.find((v) => v.value === value)}
            error={error}
        />
    );
}

export function SelectPortal() {
    return (
        <div
            id='select-portal'
            style={{ position: 'absolute', zIndex: z.dropdown }}
            data-test-component='ui/forms/SelectPortal'
        />
    );
}

// checks if value given in props is an object (assume of type  SelectOption) or array of object if isMulti
function isUnsupportedValue<T extends string | number>(props: SelectProps<T>): boolean {
    if (props.value === undefined || props.value === null) return false;

    let isUnsupported: boolean;
    if (props.isMulti) {
        isUnsupported =
            Array.isArray(props.value) && props.value.some((v) => typeof v !== 'object');
    } else {
        isUnsupported =
            props.value !== undefined && props.value !== null && typeof props.value !== 'object';
    }

    return isUnsupported;
}

// builds a SelectOption or SelectOption[] from primitive value(s) given in props
// creates a list of all possible options and find and return the one(s) matching the value(s)
function buildSupportedValue<T extends string | number>(
    props: SelectProps<T>,
): SelectOption<T> | SelectOption<T>[] | undefined {
    // all possible options (flattened in case of groups) and usign defaultOptions if present
    const options = (props.defaultOptions || props.options || []).flatMap((o) => {
        if (typeof o === 'object') {
            if (Object.hasOwn(o, 'options')) {
                return (o as GroupBase<SelectOption>).options;
            } else {
                return o as SelectOption;
            }
        } else {
            return { label: String(o), value: o };
        }
    }) as SelectOption<T>[];

    if (props.isMulti) {
        if (Array.isArray(props.value)) {
            return props.value.map((v) => toSelectValue(options, v));
        }
        return undefined;
    } else {
        if (props.value !== undefined && props.value !== null) {
            return toSelectValue(options, props.value as string | number | SelectOption<T>);
        }
        return undefined;
    }
}

// note that we only check for simple list of options, other variants as grouped or defaultOptions must be changed in the implementation.
function isUnsupportedOptions<T extends string | number>(props: SelectProps<T>): boolean {
    const optionsAreGrouped =
        typeof props.options?.[0] === 'object' && Object.hasOwn(props.options[0], 'options');

    if (!props.options || optionsAreGrouped) return false;

    const isUnsupported = props.options.some((o) => typeof o !== 'object');

    if (isUnsupported) {
        reportError(
            `Unexpected options format in Select component. Options: ${JSON.stringify(props.options)}`,
            'error',
        );
    }

    return isUnsupported;
}

Select.Skeleton = function SelectSkeleton({ className = '' }: { className?: string }) {
    return (
        <div
            className={classNames({
                'select-input select-skeleton': true,
                [className]: Boolean(className),
            })}></div>
    );
};
